import React, { useRef, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useOutletContext } from "react-router-dom";
import { Select, useResizeObserver } from "@activecollab/components";
import PropTypes from "prop-types";
import { AGENDA } from "~/components/Calendar/Agenda/Agenda";
import { USER_PROFILE_TASK_PAGE } from "~/pages/People/UserProfile/UserProfileTasksPage";
import { editPersonalizedConfigOptions } from "~/store/actions/actions.personalizedConfigOptions";
import { getPersonalizedConfigOption } from "~/store/selectors/selector.configOptions";
import { getProjectsForPicker } from "~/store/selectors/selector.projects";
import { useConversationContentContext } from "../Chat/context";
import { CREATE_TASK_MODE } from "../Chat/Hub/ConversationContent";
import { MY_ADD_MENU } from "../MyWork/MyAddMenu";
import { ProjectsPickerGroupBy } from "../ProjectsPickerGroupBy/ProjectsPickerGroupBy";
import { useCreateTaskContext } from "../Providers/CreateTask/context";

const STRICT_MODES_SOURCE = [MY_ADD_MENU, USER_PROFILE_TASK_PAGE, AGENDA];

export const ProjectList = ({
  onClick,
  onHeightChange,
  style,
  onlyWithTrackingEnabled,
  focusInput,
  type,
  selected,
  autoHeightMax,
}) => {
  const ref = useRef(null);
  const dispatch = useDispatch();
  const dimensions = useResizeObserver(ref);
  const height = dimensions?.height;
  const { t } = useTranslation();
  const data = useOutletContext();
  const groupBy = useSelector((state) => getPersonalizedConfigOption(state, "projects_picker_group_by"));
  const { mode } = useConversationContentContext();
  const { modalOpenFrom } = useCreateTaskContext();

  const filterStrictProjects = useMemo(
    () => STRICT_MODES_SOURCE.includes(modalOpenFrom) || mode === CREATE_TASK_MODE,
    [modalOpenFrom, mode]
  );
  const projectsListIds = useMemo(
    () =>
      data?.userProjectsData?.data.pages
        .flatMap((page) => page.projects)
        .filter((project) => !project.is_completed)
        .map((project) => project.id),
    [data?.userProjectsData?.data.pages]
  );

  const projects = useSelector((state) =>
    // if we want all projects 'onlyWithTrackingEnabled' needs to be undefined
    getProjectsForPicker(state, groupBy, onlyWithTrackingEnabled, projectsListIds, filterStrictProjects)
  );

  const handleChangeGroupBy = useCallback(
    (value) => {
      dispatch(editPersonalizedConfigOptions({ projects_picker_group_by: value }));
    },
    [dispatch]
  );

  useEffect(() => {
    if (height && height > 0) {
      onHeightChange(height);
    }
  }, [height, onHeightChange]);

  return (
    <div
      ref={ref}
      style={style}
    >
      <Select
        options={projects}
        focusInput={focusInput}
        onChange={onClick}
        noResultText={t("No Projects")}
        placeholder={t("Search a project")}
        type={type}
        selected={selected}
        autoHeightMax={autoHeightMax}
        endAdornment={
          <ProjectsPickerGroupBy
            onChange={handleChangeGroupBy}
            selected={groupBy}
          />
        }
      />
    </div>
  );
};

ProjectList.propTypes = {
  onClick: PropTypes.func,
  onHeightChange: PropTypes.func,
  style: PropTypes.object,
  onlyWithTrackingEnabled: PropTypes.bool,
  focusInput: PropTypes.bool,
  type: PropTypes.string,
  selected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  autoHeightMax: PropTypes.number,
};

ProjectList.defaultProps = {
  onlyWithTrackingEnabled: undefined,
  type: "single",
  selected: [],
};

ProjectList.displayName = `ProjectList`;
